import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { usePartnerSettings } from '@/hooks/usePartnerSettings';
import { Separator } from '@/components/ui/separator';

interface FieldConfig {
  enabled_fields: string[];
  required_fields: string[];
}

const FIELD_CATEGORIES = {
  'Trade & Specialty': [
    { id: 'trade_specialties', label: 'Trade Specialties' },
    { id: 'union_status', label: 'Union Status' },
    { id: 'service_radius_miles', label: 'Service Radius (Miles)' },
  ],
  'Compliance & Legal': [
    { id: 'license_number', label: 'License Number' },
    { id: 'license_state', label: 'License State' },
    { id: 'license_expiry_date', label: 'License Expiry Date' },
    { id: 'tax_id', label: 'Tax ID' },
    { id: 'insurance_general_liability_amount', label: 'General Liability Insurance' },
    { id: 'insurance_workers_comp_amount', label: "Workers' Comp Insurance" },
    { id: 'insurance_expiry_date', label: 'Insurance Expiry Date' },
  ],
  'Business Details': [
    { id: 'years_in_business', label: 'Years in Business' },
    { id: 'employees_count', label: 'Number of Employees' },
    { id: 'annual_revenue', label: 'Annual Revenue' },
    { id: 'payment_terms', label: 'Payment Terms' },
  ],
  'Performance Tracking': [
    { id: 'prequalification_status', label: 'Prequalification Status' },
    { id: 'prequalification_expiry_date', label: 'Prequalification Expiry' },
    { id: 'safety_rating', label: 'Safety Rating' },
    { id: 'quality_rating', label: 'Quality Rating' },
    { id: 'reliability_rating', label: 'Reliability Rating' },
  ],
};

export function FieldConfigurationSettings() {
  const { settings, isLoading, updateSettings } = usePartnerSettings();
  const [contractorConfig, setContractorConfig] = useState<FieldConfig>({
    enabled_fields: [],
    required_fields: [],
  });
  const [vendorConfig, setVendorConfig] = useState<FieldConfig>({
    enabled_fields: [],
    required_fields: [],
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings?.field_config) {
      setContractorConfig(settings.field_config.contractor || { enabled_fields: [], required_fields: [] });
      setVendorConfig(settings.field_config.vendor || { enabled_fields: [], required_fields: [] });
    }
  }, [settings]);

  const handleToggleEnabled = (type: 'contractor' | 'vendor', fieldId: string) => {
    const config = type === 'contractor' ? contractorConfig : vendorConfig;
    const setConfig = type === 'contractor' ? setContractorConfig : setVendorConfig;
    
    const enabled = config.enabled_fields.includes(fieldId);
    const newEnabledFields = enabled
      ? config.enabled_fields.filter(id => id !== fieldId)
      : [...config.enabled_fields, fieldId];
    
    const newRequiredFields = enabled
      ? config.required_fields.filter(id => id !== fieldId)
      : config.required_fields;

    setConfig({
      enabled_fields: newEnabledFields,
      required_fields: newRequiredFields,
    });
    setHasChanges(true);
  };

  const handleToggleRequired = (type: 'contractor' | 'vendor', fieldId: string) => {
    const config = type === 'contractor' ? contractorConfig : vendorConfig;
    const setConfig = type === 'contractor' ? setContractorConfig : setVendorConfig;
    
    const required = config.required_fields.includes(fieldId);
    const newRequiredFields = required
      ? config.required_fields.filter(id => id !== fieldId)
      : [...config.required_fields, fieldId];

    setConfig({
      ...config,
      required_fields: newRequiredFields,
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    await updateSettings({
      field_config: {
        contractor: contractorConfig,
        vendor: vendorConfig,
      },
    });
    setHasChanges(false);
  };

  if (isLoading) {
    return <Card><CardContent className="py-6">Loading...</CardContent></Card>;
  }

  const renderFieldList = (type: 'contractor' | 'vendor') => {
    const config = type === 'contractor' ? contractorConfig : vendorConfig;

    return (
      <div className="space-y-6">
        {Object.entries(FIELD_CATEGORIES).map(([category, fields]) => (
          <div key={category} className="space-y-3">
            <h4 className="font-medium text-sm">{category}</h4>
            <div className="space-y-2 pl-4">
              {fields.map((field) => {
                const isEnabled = config.enabled_fields.includes(field.id);
                const isRequired = config.required_fields.includes(field.id);

                return (
                  <div key={field.id} className="flex items-center justify-between py-2">
                    <span className="text-sm">{field.label}</span>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`${type}-${field.id}-enabled`}
                          checked={isEnabled}
                          onCheckedChange={() => handleToggleEnabled(type, field.id)}
                        />
                        <Label 
                          htmlFor={`${type}-${field.id}-enabled`}
                          className="text-sm cursor-pointer"
                        >
                          Enabled
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`${type}-${field.id}-required`}
                          checked={isRequired}
                          disabled={!isEnabled}
                          onCheckedChange={() => handleToggleRequired(type, field.id)}
                        />
                        <Label 
                          htmlFor={`${type}-${field.id}-required`}
                          className={`text-sm cursor-pointer ${!isEnabled && 'opacity-50'}`}
                        >
                          Required
                        </Label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Field Configuration</CardTitle>
        <CardDescription>
          Control which fields appear in the company form for contractors and vendors
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Contractor Fields</h3>
              <div className="text-xs text-muted-foreground">
                {contractorConfig.enabled_fields.length} enabled, {contractorConfig.required_fields.length} required
              </div>
            </div>
            <Separator />
            {renderFieldList('contractor')}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Vendor Fields</h3>
              <div className="text-xs text-muted-foreground">
                {vendorConfig.enabled_fields.length} enabled, {vendorConfig.required_fields.length} required
              </div>
            </div>
            <Separator />
            {renderFieldList('vendor')}
          </div>
        </div>

        <Button onClick={handleSave} disabled={!hasChanges}>
          Save Configuration
        </Button>
      </CardContent>
    </Card>
  );
}
